/**
 * VirulentRemainsPassiveSkillEffect — Poison tree expel path terminal passive.
 *
 * When an enemy dies while carrying `poison`, spawns a `carrier_cloud`
 * toxic zone at the death position. Unlike Carrier (plague path), the
 * cloud duration is always flat — no scaling with remaining DoT. The
 * corpse just exhales a fixed 4-second cloud regardless of how much
 * poison remained.
 *
 * Mechanically identical to Carrier's registration pattern: on
 * construction, pushes a `DeathTriggerConfig` into the
 * `DeathTriggerSystem` with `requireStatus: 'poison'` and a `spawnZone`
 * effect. The condition is `requireStatus` (enemy has poison at death),
 * not `requireKillByStatus` — so any damage source triggers the cloud
 * as long as poison was active.
 *
 * Mild in isolation. When Carrier (plague path) and Dormancy layer on
 * top in cross-tree hotbar builds, the dead generate an autonomous
 * contamination network while the player supervises from a distance.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 * @see poison-spec.md § Virulent Remains (corpse_bloom_passive)
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { DeathTriggerConfig } from '../../../systems/DeathTriggerSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface VirulentRemainsPassiveDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
  readonly deathTriggers: {
    register(config: DeathTriggerConfig): void;
    unregister(triggerId: string): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface VirulentRemainsPassiveConfig {
  readonly defaultRadiusTiles: number;
  readonly defaultDurationMs: number;
}

const DEFAULT_CONFIG: VirulentRemainsPassiveConfig = {
  defaultRadiusTiles: 1.5,
  defaultDurationMs: 4000,
};

// ---------------------------------------------------------------------------
// Trigger ID constant
// ---------------------------------------------------------------------------

const TRIGGER_ID = 'virulent_remains_cloud_spawn';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class VirulentRemainsPassiveSkillEffect implements SkillEffect {
  readonly skillId = 'corpse_bloom_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: VirulentRemainsPassiveDeps;
  private readonly config: VirulentRemainsPassiveConfig;

  private _deathTriggerRegistered = false;

  constructor(
    deps: VirulentRemainsPassiveDeps,
    config: Partial<VirulentRemainsPassiveConfig> = {},
  ) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Passive — register death trigger immediately on construction
    this.registerDeathTrigger();
  }

  // --- State getters ---

  get deathTriggerRegistered(): boolean { return this._deathTriggerRegistered; }

  /** Cloud radius in tiles, resolved from the upgrade tree with config fallback. */
  get radiusTiles(): number {
    const resolved = this.deps.resolver.getAdditiveEffect(
      'corpse_bloom_passive', 'poisonDeathCloudRadius',
    );
    return resolved > 0 ? resolved : this.config.defaultRadiusTiles;
  }

  /** Cloud duration in ms, resolved from the upgrade tree with config fallback. */
  get durationMs(): number {
    const resolved = this.deps.resolver.getAdditiveEffect(
      'corpse_bloom_passive', 'poisonDeathCloudDurationMs',
    );
    return resolved > 0 ? resolved : this.config.defaultDurationMs;
  }

  // --- SkillEffect interface ---

  update(_delta: number): void {
    // Passive — no per-frame logic
  }

  destroy(): void {
    if (this._deathTriggerRegistered) {
      this.deps.deathTriggers.unregister(TRIGGER_ID);
      this._deathTriggerRegistered = false;
    }
  }

  // --- Internal ---

  private registerDeathTrigger(): void {
    if (this._deathTriggerRegistered) return;

    this.deps.deathTriggers.register({
      id: TRIGGER_ID,
      sourceSkillId: 'corpse_bloom_passive',
      requireStatus: 'poison',
      spawnZone: {
        zoneType: 'carrier_cloud',
        radiusTiles: this.config.defaultRadiusTiles,
        durationMs: this.config.defaultDurationMs,
      },
    });

    this._deathTriggerRegistered = true;
  }
}
